import React, { useEffect, useMemo, useRef, useState } from "react";

import useLineStore from "./squiggleStore";
import AnimatedArrow from "./Arrow";

// Utility to create squiggle path
const createSquigglePath = (x1, y1, x2, y2) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const distance = Math.sqrt(dx * dx + dy * dy);

  // Normalize to unit vector
  const unitX = dx / distance;
  const unitY = dy / distance;

  // Create x spacing array similar to original - tighter squiggles
  const minVal = 8;
  const maxVal = 18;
  const t = Math.min((distance - 100) / 200, 1);
  const allowedMax = Math.round(minVal + t * (maxVal - minVal));

  let first = Math.floor(Math.random() * (allowedMax - minVal + 1)) + minVal;
  let second = first + Math.floor(Math.random() * 3) + 1;

  if (second > allowedMax) {
    second = allowedMax;
    first = Math.max(minVal, second - Math.floor(Math.random() * 3) - 1);
  }

  const xArray = [Math.min(first, second), Math.max(first, second)];

  // Y offsets for the curve control points (perpendicular to line direction)
  const yArray = [10, 30, 20];

  let path = [`M${x1},${y1}`];
  let distAlongLine = Math.ceil(Math.random() * 8) + 10;

  while (distAlongLine < distance - xArray[0] - xArray[1] - xArray[1]) {
    // Calculate base position along the line
    const baseX = x1 + unitX * distAlongLine;
    const baseY = y1 + unitY * distAlongLine;

    // Perpendicular offset (rotate unit vector 90 degrees)
    const perpX = -unitY;
    const perpY = unitX;

    // First control point
    const cp1x = baseX + perpX * yArray[0];
    const cp1y = baseY + perpY * yArray[0];
    distAlongLine += xArray[0];

    // Second control point
    const cp2x = x1 + unitX * distAlongLine + perpX * yArray[1];
    const cp2y = y1 + unitY * distAlongLine + perpY * yArray[1];
    distAlongLine += xArray[1];

    // End point of curve
    const endX = x1 + unitX * distAlongLine + perpX * yArray[2];
    const endY = y1 + unitY * distAlongLine + perpY * yArray[2];
    distAlongLine += xArray[1];

    path.push(`C${cp1x},${cp1y} ${cp2x},${cp2y} ${endX},${endY}`);
  }

  // Final segment to endpoint if we have room
  if (distance - distAlongLine >= 10) {
    const midDist = distAlongLine + (distance - distAlongLine) * 0.4;
    const midX = x1 + unitX * midDist;
    const midY = y1 + unitY * midDist;
    const perpX = -unitY;
    const perpY = unitX;

    path.push(`Q${midX + perpX * 3},${midY + perpY * 3} ${x2},${y2}`);
  } else {
    path.push(`L${x2},${y2}`);
  }

  return path.join(" ");
};

const AnimatedSquiggle = ({ line, isLatest }) => {
  const [pathLength, setPathLength] = useState(0);
  const pathRef = useRef(null);
  const [elapsed, setElapsed] = useState(0);
  const [fadeStartTime, setFadeStartTime] = useState(null);
  const [, forceUpdate] = useState(0);
  const wasLatest = useRef(isLatest);

  const path = useMemo(() => {
    return createSquigglePath(line.from.x, line.from.y, line.to.x, line.to.y);
  }, [line.from.x, line.from.y, line.to.x, line.to.y]);

  useEffect(() => {
    if (pathRef.current) {
      const length = pathRef.current.getTotalLength();
      setPathLength(length);
    }
  }, [path]);

  // Detect when this line becomes non-latest
  useEffect(() => {
    if (wasLatest.current && !isLatest) {
      // This line just became non-latest, start fading immediately
      setFadeStartTime(Date.now());
    }
    wasLatest.current = isLatest;
  }, [isLatest]);

  // Animate the fade when this line becomes non-latest
  useEffect(() => {
    if (!fadeStartTime) return;

    const fadeOutDuration = 800;
    const animate = () => {
      const fadeElapsed = Date.now() - fadeStartTime;
      if (fadeElapsed < fadeOutDuration) {
        requestAnimationFrame(animate);
        // Force re-render
        forceUpdate((n) => n + 1);
      }
    };

    requestAnimationFrame(animate);
  }, [fadeStartTime]);

  useEffect(() => {
    const startTime = Date.now();
    const animate = () => {
      const current = Date.now() - startTime;
      setElapsed(current);

      if (current < line.duration) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [line.duration]);

  // Animation phases:
  // 1. Drawing phase: line draws in from start to finish (800ms)
  // 2. Full visibility: entire line stays visible (2000ms)
  // 3. Disappearing ink: line disappears from start to finish (800ms)
  const drawDuration = 800;
  const holdDuration = 2000;
  const fadeOutDuration = 800;

  let offset;
  let dasharray;

  if (elapsed < drawDuration) {
    // Phase 1: Drawing animation - line draws in
    const drawProgress = elapsed / drawDuration;
    // Start with offset = pathLength (nothing visible), end at offset = 0 (fully visible)
    offset = pathLength - drawProgress * pathLength;
    dasharray = pathLength;
  } else if (elapsed < drawDuration + holdDuration) {
    // Phase 2: Show entire line
    offset = 0;
    dasharray = pathLength;
  } else {
    // Phase 3: Fade out entire line
    offset = 0;
    dasharray = pathLength;
  }

  // Calculate opacity
  let opacity = 1;
  if (pathLength === 0) {
    // Hide until we have pathLength calculated (prevents initial flash)
    opacity = 0;
  } else {
    // Calculate natural fade progress
    let naturalOpacity = 1;
    if (elapsed >= drawDuration + holdDuration) {
      const fadeProgress =
        (elapsed - drawDuration - holdDuration) / fadeOutDuration;
      naturalOpacity = 1 - fadeProgress;
    }

    // Calculate immediate fade if not latest
    let immediateOpacity = 1;
    if (!isLatest && fadeStartTime) {
      const fadeElapsed = Date.now() - fadeStartTime;
      const fadeProgress = Math.min(fadeElapsed / fadeOutDuration, 1);
      immediateOpacity = 1 - fadeProgress;
    }

    // Use whichever is more faded (lower opacity)
    opacity = Math.min(naturalOpacity, immediateOpacity);
  }

  return (
    <path
      ref={pathRef}
      d={path}
      stroke={line.color}
      strokeWidth="3"
      fill="none"
      strokeDasharray={dasharray}
      strokeDashoffset={offset}
      strokeLinecap="round"
      style={{
        filter: "drop-shadow(1px 1px 1px rgba(0,0,0,0.3))",
        opacity: opacity,
      }}
    />
  );
};

// Main global component
const Squiggle = () => {
  const lines = useLineStore((state) => state.lines);

  // Find the most recent line
  const latestLineId =
    lines.length > 0
      ? lines.reduce((latest, line) =>
          line.createdAt > latest.createdAt ? line : latest
        ).id
      : null;

  return (
    <svg
      data-cy="Squiggle_Container"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 9999,
      }}
    >
      {lines.map((line) => {
        const Component =
          line.variant === "squiggle" ? AnimatedSquiggle : AnimatedArrow;
        return (
          <Component
            key={line.id}
            line={line}
            isLatest={line.id === latestLineId}
          />
        );
      })}
    </svg>
  );
};

export default Squiggle;
